/**
 * Backtest Types
 *
 * TypeScript interfaces for covered call backtesting API.
 */

// Delta target types
export type DeltaType = 'defensive' | 'balanced' | 'income';

// DTE range types
export type DteRangeType = 'weekly' | 'biweekly' | 'monthly';

// Labels for display
export const DELTA_LABELS: Record<DeltaType, string> = {
  defensive: 'Defensive (δ0.18)',
  balanced: 'Balanced (δ0.25)',
  income: 'Income (δ0.35)'
};

export const DTE_LABELS: Record<DteRangeType, string> = {
  weekly: 'Weekly (5-12d)',
  biweekly: 'Bi-weekly (14-30d)',
  monthly: 'Monthly (30-45d)'
};

// Request types
export interface BacktestRequest {
  symbol: string;
  start_date: string;  // ISO date format
  end_date: string;
  initial_capital: number;
  cash_yield_rate: number;
  entry_behavior: 'auto_enter' | 'trend_filtered' | 'compare';  // Entry filter behavior
  hull_buffer: 'no_buffer' | 'with_buffer' | 'compare';  // Hull breach buffer behavior
  deltas: DeltaType[];      // Delta targets to test
  dte_ranges: DteRangeType[];  // DTE ranges to test
}

// Trade result from a completed backtest trade
export interface TradeResult {
  entry_date: string;
  exit_date: string;
  entry_price: number;
  exit_price: number;
  entry_premium: number;
  exit_premium: number;
  strike_price: number;
  entry_delta: number | null;  // Delta at entry
  entry_dte: number;           // Days to expiration at entry
  pnl_stock: number;
  pnl_option: number;
  pnl_total: number;
  exit_reason: 'close' | 'roll' | 'assign' | 'expire_otm' | 'end_of_backtest';
  days_held: number;
}

// Daily portfolio value snapshot
export interface DailyValueResult {
  date: string;
  stock_value: number;
  option_liability: number;
  cash: number;
  total_value: number;
}

// Results for a single strategy (key is now "delta_dte" format, e.g., "balanced_monthly")
export interface StrategyResult {
  strategy: string;  // Dynamic key like "balanced_monthly" or "defensive_weekly_filtered"
  total_return: number;
  total_return_pct: number;
  annualized_return: number;
  max_drawdown: number | null;  // negative % (glossary max_drawdown, same formula as live)
  sharpe_ratio: number | null;
  total_trades: number;
  win_rate: number;
  avg_win: number;
  avg_loss: number;
  profit_factor: number;
  trades: TradeResult[];
  daily_values: DailyValueResult[];
}

// Benchmark daily value
export interface BenchmarkDailyValue {
  date: string;
  return_pct: number;
}

// Benchmark comparison results
export interface BenchmarkResult {
  symbol: string;
  total_return: number;
  total_return_pct: number;
  annualized_return: number;
  max_drawdown: number | null;  // negative % (glossary max_drawdown, same formula as live)
  daily_values: BenchmarkDailyValue[];
}

// Summary of strategy comparison
export interface ComparisonSummary {
  best_strategy: string;
  best_return_pct: number;
  vs_spy_alpha: number;
  vs_spyi_alpha: number;
}

// Technical data for entry restriction visualization
export interface TechnicalDailyData {
  date: string;
  price: number;
  ma200: number | null;
  rsi: number | null;
  blocked: boolean;
  block_reason: 'trend' | 'rsi' | null;
}

export interface BlockedZone {
  start: string;
  end: string;
}

export interface TechnicalData {
  daily: TechnicalDailyData[];
  blocked_zones: BlockedZone[];
}

// Complete backtest response
export interface BacktestResponse {
  symbol: string;
  period: {
    start: string;
    end: string;
  };
  strategies: Record<string, StrategyResult>;  // Dynamic keys for filtered strategies
  benchmarks: Record<string, BenchmarkResult>;  // Dynamic: includes stock, SPY, SPYI
  comparison_summary: ComparisonSummary;
  technical_data?: TechnicalData;  // Entry restriction zones data
}

// Available symbols response
export interface AvailableSymbolsResponse {
  symbols: string[];
  count: number;
}

// Strategy configuration info
export interface StrategyInfo {
  name: string;
  target_delta: number;
  min_dte: number;
  max_dte: number;
  description: string;
}

export interface StrategyInfoResponse {
  strategies: {
    defensive: StrategyInfo;
    recommended: StrategyInfo;
    income: StrategyInfo;
  };
  position_rules: {
    [key: string]: string;
  };
}

// Form state for backtest input
export interface BacktestFormState {
  symbol: string;
  startDate: Date | null;
  endDate: Date | null;
  initialCapital: number;
  cashYieldRate: number;
  entryBehavior: 'auto_enter' | 'trend_filtered' | 'compare';
}

// Chart data point for performance visualization
export interface ChartDataPoint {
  date: string;
  defensive: number;
  recommended: number;
  income: number;
  SPY: number;
  SPYI: number;
}

// Helper to format strategy name for display
// Parses keys like "balanced_monthly", "defensive_weekly_filtered", "income_biweekly_buffered"
export const formatStrategyName = (key: string): string => {
  // Parse key: delta_dte[_filtered][_buffered]
  const parts = key.split('_');

  // Delta mapping
  const deltaMap: Record<string, string> = {
    defensive: 'δ0.18',
    balanced: 'δ0.25',
    income: 'δ0.35'
  };

  // DTE mapping
  const dteMap: Record<string, string> = {
    weekly: '5-12d',
    biweekly: '14-30d',
    monthly: '30-45d'
  };

  // Extract delta and DTE from parts
  const delta = parts[0];
  const dte = parts[1];

  // Build base name
  let name = `${deltaMap[delta] || delta} ${dteMap[dte] || dte}`;

  // Add suffixes
  if (key.includes('_filtered')) {
    name += ' (Filtered)';
  }
  if (key.includes('_buffered')) {
    name += ' (Buffered)';
  }

  return name;
};

// Helper to format exit reason for display
export const formatExitReason = (reason: string): string => {
  switch (reason) {
    case 'close':
      return 'Closed Early';
    case 'roll':
      return 'Rolled';
    case 'assign':
      return 'Assigned';
    case 'expire_otm':
      return 'Expired OTM';
    case 'end_of_backtest':
      return 'End of Period';
    default:
      return reason;
  }
};

// Helper to get strategy color
// Color based on delta + modifiers for filtered/buffered
export const getStrategyColor = (key: string): string => {
  // Handle benchmarks
  if (key === 'SPY') return '#6B7280';  // Gray
  if (key === 'SPYI') return '#8B5CF6'; // Purple

  // Parse the key to get delta
  const parts = key.split('_');
  const delta = parts[0];
  const isFiltered = key.includes('_filtered');
  const isBuffered = key.includes('_buffered');

  // Base colors for each delta
  const baseColors: Record<string, string> = {
    defensive: '#10B981', // Green
    balanced: '#3B82F6',  // Blue
    income: '#F59E0B'     // Amber
  };

  // Darker variants for filtered
  const filteredColors: Record<string, string> = {
    defensive: '#059669',
    balanced: '#2563EB',
    income: '#D97706'
  };

  // Even darker for filtered + buffered
  const filteredBufferedColors: Record<string, string> = {
    defensive: '#047857',
    balanced: '#1D4ED8',
    income: '#B45309'
  };

  // Lighter for just buffered
  const bufferedColors: Record<string, string> = {
    defensive: '#34D399',
    balanced: '#60A5FA',
    income: '#FCD34D'
  };

  if (isFiltered && isBuffered) {
    return filteredBufferedColors[delta] || '#6B7280';
  } else if (isFiltered) {
    return filteredColors[delta] || '#6B7280';
  } else if (isBuffered) {
    return bufferedColors[delta] || '#6B7280';
  }
  return baseColors[delta] || '#6B7280';
};

// Progress tracking types for SSE streaming
export type ProgressStepId = 'data' | 'defensive' | 'balanced' | 'income' | 'benchmarks';

export interface BacktestProgress {
  step: ProgressStepId | 'complete' | 'error';
  progress: number;  // 0-100
  message: string;
  result?: BacktestResponse;  // Only present when step === 'complete'
}

export interface ProgressStep {
  id: ProgressStepId;
  label: string;
  status: 'pending' | 'running' | 'complete';
  progress: number;
}

// Default progress steps configuration
export const DEFAULT_PROGRESS_STEPS: ProgressStep[] = [
  { id: 'data', label: 'Fetching historical data', status: 'pending', progress: 0 },
  { id: 'defensive', label: 'Simulating Defensive (δ0.18)', status: 'pending', progress: 0 },
  { id: 'balanced', label: 'Simulating Balanced (δ0.25)', status: 'pending', progress: 0 },
  { id: 'income', label: 'Simulating Income (δ0.35)', status: 'pending', progress: 0 },
  { id: 'benchmarks', label: 'Calculating benchmarks', status: 'pending', progress: 0 },
];

// Generate progress steps based on selected deltas
export const getProgressSteps = (deltas: DeltaType[]): ProgressStep[] => {
  const steps: ProgressStep[] = [
    { id: 'data', label: 'Fetching historical data', status: 'pending', progress: 0 },
  ];

  if (deltas.includes('defensive')) {
    steps.push({ id: 'defensive', label: 'Simulating Defensive (δ0.18)', status: 'pending', progress: 0 });
  }
  if (deltas.includes('balanced')) {
    steps.push({ id: 'balanced', label: 'Simulating Balanced (δ0.25)', status: 'pending', progress: 0 });
  }
  if (deltas.includes('income')) {
    steps.push({ id: 'income', label: 'Simulating Income (δ0.35)', status: 'pending', progress: 0 });
  }

  steps.push({ id: 'benchmarks', label: 'Calculating benchmarks', status: 'pending', progress: 0 });

  return steps;
};

// SSE status event for Mission Control UI
export interface BacktestStatusEvent {
  type: 'status';
  current_date: string;      // ISO date: "2024-12-14"
  trades_count: number;
  wins_count: number;
  pnl_running: number;
}

// Live stats state for Mission Control
export interface LiveStats {
  currentDate: string;       // Formatted for display: "DEC 14, 2024"
  tradesCount: number;
  winsCount: number;
  winRate: number;           // Calculated: (wins/trades) * 100
  pnlRunning: number;
  eventsPerSecond: number;   // Calculated from log frequency
}

// Default live stats
export const DEFAULT_LIVE_STATS: LiveStats = {
  currentDate: '',
  tradesCount: 0,
  winsCount: 0,
  winRate: 0,
  pnlRunning: 0,
  eventsPerSecond: 0
};
